/**
 * Guest → auth handoff for training geo alerts (state/county intent preserved in sessionStorage).
 */

export const TRAINING_GEO_ALERT_GUEST_INTENT_KEY = 'apt_training_geo_alert_guest_intent_v1';

export const TRAINING_GEO_ALERT_RESUME_QUERY = 'trainingGeoAlerts';

export const TRAINING_GEO_ALERT_RESUME_VALUE = 'resume';

export type TrainingGeoAlertGuestIntent = {
  geoStateId?: number | null;
  countyIds?: number[];
  /** e.g. events_filter, events_empty, event_detail */
  source?: string;
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

function normalizeIntent(
  raw: TrainingGeoAlertGuestIntent | null | undefined,
): TrainingGeoAlertGuestIntent | null {
  if (!raw || typeof raw !== 'object') return null;
  const geoStateId =
    raw.geoStateId != null && Number.isFinite(Number(raw.geoStateId))
      ? Number(raw.geoStateId)
      : null;
  const countyIds = Array.isArray(raw.countyIds)
    ? raw.countyIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];
  if (geoStateId == null && countyIds.length === 0 && !raw.source) return null;
  return {
    geoStateId,
    countyIds,
    source: typeof raw.source === 'string' ? raw.source : undefined,
  };
}

export function saveTrainingGeoAlertGuestIntent(
  intent: TrainingGeoAlertGuestIntent,
): void {
  if (!isBrowser()) return;
  const normalized = normalizeIntent(intent);
  if (!normalized) return;
  try {
    sessionStorage.setItem(
      TRAINING_GEO_ALERT_GUEST_INTENT_KEY,
      JSON.stringify(normalized),
    );
  } catch {
    /* storage blocked */
  }
}

export function readTrainingGeoAlertGuestIntent(): TrainingGeoAlertGuestIntent | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(TRAINING_GEO_ALERT_GUEST_INTENT_KEY);
    if (!raw) return null;
    return normalizeIntent(JSON.parse(raw) as TrainingGeoAlertGuestIntent);
  } catch {
    return null;
  }
}

export function clearTrainingGeoAlertGuestIntent(): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(TRAINING_GEO_ALERT_GUEST_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

export function buildTrainingGeoAlertReturnPath(
  pathname: string,
  search: string,
  hash = '',
): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  params.set(TRAINING_GEO_ALERT_RESUME_QUERY, TRAINING_GEO_ALERT_RESUME_VALUE);
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${hash}`;
}

export function shouldResumeTrainingGeoAlertsFromSearch(
  searchParams: URLSearchParams,
): boolean {
  return (
    searchParams.get(TRAINING_GEO_ALERT_RESUME_QUERY) ===
    TRAINING_GEO_ALERT_RESUME_VALUE
  );
}

export function stripTrainingGeoAlertResumeParam(
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(TRAINING_GEO_ALERT_RESUME_QUERY);
  return next;
}
